import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AlwaysOnAboutRoomModalTab from './AlwaysOnAboutRoomModalTab'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import zIndex from 'styles/tools/z-index'
import usePropertyBedbankViews from 'hooks/OfferPage/usePropertyBedbankViews'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useOffer from 'hooks/Offers/useOffer'
import { getPackageOptionsByRoom } from 'components/OfferPage/Property/singlePropertyUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PropertyRoomOptionMobile from 'components/OfferPage/Property/MobileBookingFlow/PropertyRoomOptionMobile'
import { isHotelPackage } from 'lib/offer/offerTypes'
import uuidV4 from 'lib/string/uuidV4Utils'
import AccommodationMobileHeaderFilters from 'components/OfferPage/Accommodation/AccommodationMobileHeaderFilters'

const StickyTabButtonsGroup = styled(TabButtonsGroup)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  room?: App.PackageRoomType;
  offer: App.HotelOffer;
  airports?: Array<App.Airport>;
  selectedBedbankRoomId?: string;
  packageOptions: Array<OfferPackageOptionView>;
  onClose?: () => void;
}

function SinglePropertyRoomAndPackageDetailsModal(props: Props) {
  const {
    room,
    offer,
    airports,
    selectedBedbankRoomId,
    packageOptions,
    onClose,
  } = props

  const [selectedTab, setSelectedTab] = useState<'package-options' | 'about-room'>('package-options')

  const [bedbankPackageViews, fetchingBedbankViews] = usePropertyBedbankViews(offer.property.id)
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankOfferId)

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const packageOptionsByRoom = useMemo(() => getPackageOptionsByRoom(packageOptions, bedbankPackageViews, mapping), [packageOptions, bedbankPackageViews, mapping])
  const selectedRoom = useMemo(() => packageOptionsByRoom.find(option => option.bedbank.some(bedbank => bedbank.package.roomId === selectedBedbankRoomId) || option.lpc.some(lpc => lpc.package.roomType?.id === room?.id)), [packageOptionsByRoom, room?.id, selectedBedbankRoomId])

  // Single Property pages uses first the LPC roomType (or Bedbank) package name
  const roomName = useMemo(
    () =>
      room?.name ??
      selectedRoom?.lpc[0]?.package.roomType.name ??
      selectedRoom?.bedbank[0].package.name,
    [room, selectedRoom],
  )

  const pkg = (
    selectedRoom?.lpc[0]?.package ??
    selectedRoom?.bedbank[0].package
  ) as (App.HotelPackage | App.BedbankPackage)
  const isLpcPackage = isHotelPackage(selectedRoom)
  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageOptionsByRoom]) // regenerate id if list of rooms changes

  const selectPackageOptionsTab = useCallback(() => {
    setSelectedTab('package-options')
  }, [])

  const selectAboutRoomTab = useCallback(() => {
    setSelectedTab('about-room')
  }, [])

  if (!pkg) return null

  return (<>
    <ModalHeader
      title={roomName}
      dismissible={false}
      onBackButtonClick={onClose}
    >
      <AccommodationMobileHeaderFilters />
    </ModalHeader>
    <ModalBody>
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage roomPackage={pkg} showBookmark={isLpcPackage} />
      </AccommodationRoomTile>
      <StickyTabButtonsGroup fit="grow">
        <TabButton size="medium" isActive={selectedTab === 'package-options'} onClick={selectPackageOptionsTab}>
          Package options
        </TabButton>
        <TabButton size="medium" isActive={selectedTab === 'about-room'} onClick={selectAboutRoomTab}>
          About the room
        </TabButton>
      </StickyTabButtonsGroup>
      {selectedTab === 'package-options' && <div data-testid="rate-selectedRoom">
        {selectedRoom && <PropertyRoomOptionMobile
          key={selectedRoom.roomId}
          offer={offer}
          packageViews={selectedRoom.lpc}
          bedbankViews={selectedRoom.bedbank}
          bedbankOffer={bedbankOffer}
          flightDestination={destinationAirport}
          roomListId={roomListId}
        />}
        {fetchingBedbankViews && <LoadingIndicator inline />}
      </div>}
      {selectedTab === 'about-room' && <AlwaysOnAboutRoomModalTab pkg={pkg} />}
    </ModalBody>
  </>)
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  offer: state.offer,
  airports: appState.geo.airports,
  selectedBedbankRoomId: state.selectedBedbankRoomId,
  packageOptions: getDurationPackageOptions(state, appState),
}), SinglePropertyRoomAndPackageDetailsModal)
